import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import {
  addEmailDomain,
  deleteEmailDomain,
  getDeliverabilityOverview,
  listBounces,
  updateTrackingSettings,
  verifyEmailDomain,
} from "@/api/deliverability";
import {
  SettingsPageHeader,
  SettingsSection,
} from "@/components/settings/settings-ui";

export function SettingsDeliverabilityPage() {
  const location = useLocation();
  const qc = useQueryClient();
  const path = location.pathname;
  const [domainInput, setDomainInput] = useState("");
  const [message, setMessage] = useState<string | null>(null);

  const overview = useQuery({
    queryKey: ["deliverability", "overview"],
    queryFn: getDeliverabilityOverview,
  });

  const bounces = useQuery({
    queryKey: ["deliverability", "bounces"],
    queryFn: listBounces,
    enabled: path.includes("bounce"),
  });

  const trackingMutation = useMutation({
    mutationFn: updateTrackingSettings,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deliverability"] });
      setMessage("Tracking settings saved.");
    },
  });

  const addDomain = useMutation({
    mutationFn: () => addEmailDomain(domainInput),
    onSuccess: () => {
      setDomainInput("");
      qc.invalidateQueries({ queryKey: ["deliverability"] });
    },
  });

  const data = overview.data;
  const isDomains = path.includes("/domains");
  const isMailboxes = path.includes("/mailboxes");
  const isBounces = path.includes("bounce");

  return (
    <div>
      <SettingsPageHeader
        title="Deliverability suite"
        description="Mailbox health, sending limits, tracking subdomain, and domains — Apollo-style deliverability controls."
      />

      <div className="mb-4 flex flex-wrap gap-2 text-sm">
        <Link to="/settings/deliverability-suite/overview" className="text-brand hover:underline">
          Overview
        </Link>
        <Link to="/settings/deliverability-suite/mailboxes" className="text-brand hover:underline">
          Mailboxes
        </Link>
        <Link to="/settings/deliverability-suite/domains" className="text-brand hover:underline">
          Domains
        </Link>
        <Link
          to="/settings/deliverability-suite/bounce-logs"
          className="text-brand hover:underline"
        >
          Bounce logs
        </Link>
        <Link to="/settings/account/mailboxes" className="text-muted-foreground hover:underline">
          Manage connections
        </Link>
      </div>

      {message ? (
        <p className="mb-3 text-sm text-muted-foreground">{message}</p>
      ) : null}

      {!isDomains && !isMailboxes && !isBounces ? (
        <>
          <div className="mb-4 grid gap-3 sm:grid-cols-4">
            {(
              [
                ["Sent", data?.stats.sent],
                ["Opened", data?.stats.opened],
                ["Clicked", data?.stats.clicked],
                ["Failed", data?.stats.failed],
              ] as const
            ).map(([label, v]) => (
              <div key={label} className="rounded-lg border border-border px-3 py-2">
                <p className="text-xs text-muted-foreground">{label}</p>
                <p className="text-lg font-semibold tabular-nums">{v ?? "—"}</p>
              </div>
            ))}
          </div>

          <SettingsSection
            title="Open & click tracking"
            description="Pixels and link redirects. Use a custom tracking subdomain when possible."
          >
            <label className="mb-2 flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={data?.openTrackingEnabled !== false}
                onChange={(e) =>
                  trackingMutation.mutate({
                    emailOpenTrackingEnabled: e.target.checked,
                  })
                }
              />
              Open tracking
            </label>
            <label className="mb-2 flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={data?.clickTrackingEnabled !== false}
                onChange={(e) =>
                  trackingMutation.mutate({
                    emailClickTrackingEnabled: e.target.checked,
                  })
                }
              />
              Click tracking
            </label>
            <label className="block text-sm">
              Tracking subdomain
              <input
                className="mt-1 w-full max-w-md rounded-md border border-border bg-background px-2 py-1.5"
                defaultValue={data?.trackingSubdomain ?? ""}
                placeholder="track.yourdomain.com"
                onBlur={(e) =>
                  trackingMutation.mutate({
                    emailTrackingSubdomain: e.target.value,
                  })
                }
              />
            </label>
          </SettingsSection>
        </>
      ) : null}

      {isMailboxes || (!isDomains && !isBounces) ? (
        <SettingsSection title="Mailbox health">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground">
                <th className="py-1">Mailbox</th>
                <th>Provider</th>
                <th>Limit</th>
                <th>Sent 24h</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {(data?.mailboxes ?? []).map((m) => (
                <tr key={m.mailboxId} className="border-t border-border/60">
                  <td className="py-2">{m.emailAddress}</td>
                  <td className="capitalize">{m.provider}</td>
                  <td>
                    {m.sentLast24h ?? 0}/{m.dailySendLimit ?? 50}
                  </td>
                  <td>{m.sentLast24h ?? 0}</td>
                  <td className="capitalize">{m.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </SettingsSection>
      ) : null}

      {isDomains ? (
        <SettingsSection title="Sending domains">
          <div className="mb-3 flex gap-2">
            <input
              className="flex-1 rounded-md border border-border bg-background px-2 py-1.5 text-sm"
              placeholder="yourdomain.com"
              value={domainInput}
              onChange={(e) => setDomainInput(e.target.value)}
            />
            <button
              type="button"
              className="rounded-md bg-brand px-3 py-1.5 text-sm text-brand-foreground"
              onClick={() => addDomain.mutate()}
              disabled={!domainInput.trim() || addDomain.isPending}
            >
              Add
            </button>
          </div>
          <ul className="space-y-2 text-sm">
            {(data?.domains ?? []).map((d) => (
              <li
                key={d.domain}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border px-3 py-2"
              >
                <div>
                  <p className="font-medium">{d.domain}</p>
                  <p className="text-xs text-muted-foreground">
                    {d.status} · SPF {d.spf ? "✓" : "—"} · DKIM{" "}
                    {d.dkim ? "✓" : "—"} · DMARC {d.dmarc ? "✓" : "—"}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="text-xs text-brand hover:underline"
                    onClick={() =>
                      verifyEmailDomain(d.domain).then(() =>
                        qc.invalidateQueries({ queryKey: ["deliverability"] }),
                      )
                    }
                  >
                    Mark verified
                  </button>
                  <button
                    type="button"
                    className="text-xs text-red-600 hover:underline"
                    onClick={() =>
                      deleteEmailDomain(d.domain).then(() =>
                        qc.invalidateQueries({ queryKey: ["deliverability"] }),
                      )
                    }
                  >
                    Remove
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </SettingsSection>
      ) : null}

      {isBounces ? (
        <SettingsSection title="Bounce / failed sends">
          {bounces.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (bounces.data?.bounces.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground">No bounced or failed emails.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {bounces.data!.bounces.map((b) => (
                <li key={b.emailMessageId} className="border-b border-border/60 py-2">
                  <p className="font-medium">{b.subject}</p>
                  <p className="text-xs text-muted-foreground">
                    {b.to.join(", ")} · {b.reason || b.status}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </SettingsSection>
      ) : null}
    </div>
  );
}
